import type { WeaponId } from '../weapons/types'
import sfxDataJson from '../../assets/data/audio_sfx.json'

export type SfxPatchName =
  | 'laser'
  | 'explosion'
  | 'doorSlide'
  | 'elevatorHum'
  | 'pickupChime'
  | 'hitBeep'
  | 'click'
  | 'scannerChirp'
  | 'impact'
  | 'playerHurt'
  | string

export interface BaseSoundPatch {
  type?: 'oscillator' | 'noise' | 'fm' | 'arpeggio'
  gain?: number
  endGain?: number
  duration?: number
}

export interface OscillatorPatch extends BaseSoundPatch {
  type?: 'oscillator'
  oscillatorType?: OscillatorType
  freq?: number
  startFreq?: number
  endFreq?: number
  ramp?: 'linear' | 'exponential'
  rampDuration?: number
}

export interface NoisePatch extends BaseSoundPatch {
  type?: 'noise'
  noiseDuration?: number
  filterStartFreq?: number
  filterEndFreq?: number
  oscillatorType?: OscillatorType
  startFreq?: number
  endFreq?: number
  ramp?: 'linear' | 'exponential'
  rampDuration?: number
}

export interface FmPatch extends BaseSoundPatch {
  type?: 'fm'
  oscillatorType?: OscillatorType
  frequency?: number
  modFreq?: number
  modDepth?: number
}

export interface ArpeggioPatch extends BaseSoundPatch {
  type?: 'arpeggio'
  oscillatorType?: OscillatorType
  frequencies?: number[]
  noteDuration?: number
}

export type SoundPatch = OscillatorPatch | NoisePatch | FmPatch | ArpeggioPatch

export interface AudioSfxData {
  laser: OscillatorPatch
  explosion: NoisePatch
  doorSlide: OscillatorPatch
  elevatorHum: FmPatch
  pickupChime: ArpeggioPatch
  hitBeep: OscillatorPatch
  click: OscillatorPatch
  scannerChirp: OscillatorPatch
  impact: OscillatorPatch
  playerHurt: OscillatorPatch
  weapons: Record<string, OscillatorPatch>
  [key: string]: any
}

export class SoundEffects {
  enabled = true
  muted = false
  volume = 0.7
  readonly data: AudioSfxData = sfxDataJson as AudioSfxData
  private ctx: AudioContext | null = null
  private masterGain: GainNode | null = null

  private ensureContext(): AudioContext | null {
    if (typeof window === 'undefined' || typeof AudioContext === 'undefined') {
      return null
    }

    if (!this.ctx) {
      const AudioCtx = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext
      if (AudioCtx) {
        this.ctx = new AudioCtx()
        this.masterGain = this.ctx.createGain()
        this.masterGain.gain.setValueAtTime(this.muted ? 0 : this.volume, this.ctx.currentTime)
        this.masterGain.connect(this.ctx.destination)
      }
    }

    if (this.ctx && this.ctx.state === 'suspended') {
      this.ctx.resume()
    }

    return this.ctx
  }

  setVolume(vol: number) {
    this.volume = Math.max(0, Math.min(1, vol))
    if (this.masterGain && this.ctx && !this.muted) {
      this.masterGain.gain.setValueAtTime(this.volume, this.ctx.currentTime)
    }
  }

  toggleMute() {
    this.muted = !this.muted
    if (this.masterGain && this.ctx) {
      this.masterGain.gain.setValueAtTime(this.muted ? 0 : this.volume, this.ctx.currentTime)
    }
  }

  /**
   * Apply a one-shot envelope to a gain node: instant ramp to `startGain`
   * then exponential decay to `endGain` over `duration` seconds. Used by
   * every oscillator/noise/arpeggio/FM patch -- five identical 2-line
   * blocks previously inlined. Caller is responsible for scheduling
   * `node.start()` / `node.stop()`.
   */
  private applyEnvelope(
    gain: GainNode,
    startGain: number,
    endGain: number,
    duration: number,
    now: number,
  ): void {
    gain.gain.setValueAtTime(startGain, now)
    gain.gain.exponentialRampToValueAtTime(endGain, now + duration)
  }

  /**
   * Acquire a live AudioContext or short-circuit the caller. Centralizes the
   * `ensureContext() + guard + null check` triple that was previously inlined
   * in every play*Patch method. Returns the context and master gain, or null
   * if playback is unavailable (SSR, muted, no master).
   */
  private requireContext(): { ctx: AudioContext; masterGain: GainNode } | null {
    const ctx = this.ensureContext()
    if (!ctx || !this.masterGain || this.muted) return null
    return { ctx, masterGain: this.masterGain }
  }

  /**
   * Build the standard `osc -> gain -> master` voice used by oscillator and
   * arpeggio patches, with the oscillator type already set. Caller is
   * responsible for setting frequency, applying the envelope, and scheduling
   * start/stop on the returned oscillator. Returns null if the audio context
   * is unavailable (caller should `return` immediately).
   */
  private createOscVoice(
    masterGain: GainNode,
    type: OscillatorType,
  ): { osc: OscillatorNode; gain: GainNode; now: number } | null {
    const live = this.requireContext()
    if (!live) return null
    const { ctx } = live
    const osc = ctx.createOscillator()
    const gain = ctx.createGain()
    osc.connect(gain)
    gain.connect(masterGain)
    osc.type = type
    return { osc, gain, now: ctx.currentTime }
  }

  playPatch(patchNameOrDef: SfxPatchName | SoundPatch, volumeScale = 1.0) {
    const ctx = this.ensureContext()
    if (!ctx || !this.masterGain || this.muted) return

    let patch: SoundPatch | undefined
    if (typeof patchNameOrDef === 'string') {
      patch = (this.data as Record<string, any>)[patchNameOrDef] || this.data.weapons[patchNameOrDef]
    } else {
      patch = patchNameOrDef
    }

    if (!patch) return

    if (patch.type === 'arpeggio' || 'frequencies' in patch) {
      this.playArpeggioPatch(patch as ArpeggioPatch, volumeScale)
    } else if (patch.type === 'fm' || 'modFreq' in patch) {
      this.playFmPatch(patch as FmPatch, volumeScale)
    } else if (patch.type === 'noise' || 'filterStartFreq' in patch || 'noiseDuration' in patch) {
      this.playNoisePatch(patch as NoisePatch, volumeScale)
    } else {
      this.playOscillatorPatch(patch as OscillatorPatch, volumeScale)
    }
  }

  private playOscillatorPatch(patch: OscillatorPatch, volumeScale = 1.0) {
    const live = this.requireContext()
    if (!live) return
    const voice = this.createOscVoice(live.masterGain, patch.oscillatorType ?? 'sine')
    if (!voice) return
    const { osc, gain, now } = voice

    const startFreq = patch.startFreq ?? patch.freq ?? 440
    const endFreq = patch.endFreq ?? patch.freq ?? startFreq
    const duration = patch.duration ?? 0.1
    const rampDuration = patch.rampDuration ?? duration
    const targetGain = (patch.gain ?? 0.3) * volumeScale
    const endGain = patch.endGain ?? 0.01

    osc.frequency.setValueAtTime(startFreq, now)
    if (patch.ramp === 'linear') {
      osc.frequency.linearRampToValueAtTime(endFreq, now + rampDuration)
    } else {
      osc.frequency.exponentialRampToValueAtTime(Math.max(1, endFreq), now + rampDuration)
    }

    gain.gain.setValueAtTime(targetGain, now)
    this.applyEnvelope(gain, targetGain, endGain, duration, now)

    osc.start(now)
    osc.stop(now + duration)
  }

  private playNoisePatch(patch: NoisePatch, volumeScale = 1.0) {
    const live = this.requireContext()
    if (!live) return
    const { ctx, masterGain } = live

    const now = ctx.currentTime
    const duration = patch.noiseDuration ?? patch.duration ?? 0.45
    const targetGain = (patch.gain ?? 0.6) * volumeScale
    const endGain = patch.endGain ?? 0.01
    const filterStartFreq = patch.filterStartFreq ?? patch.startFreq ?? 120
    const filterEndFreq = patch.filterEndFreq ?? patch.endFreq ?? 20

    if (typeof ctx.createBuffer === 'function') {
      const bufferSize = Math.floor(ctx.sampleRate * duration)
      const buffer = ctx.createBuffer(1, Math.max(1, bufferSize), ctx.sampleRate)
      const data = buffer.getChannelData(0)
      for (let i = 0; i < bufferSize; i++) {
        data[i] = Math.random() * 2 - 1
      }

      const noise = ctx.createBufferSource()
      noise.buffer = buffer

      const filter = ctx.createBiquadFilter()
      filter.type = 'lowpass'
      filter.frequency.setValueAtTime(filterStartFreq, now)
      filter.frequency.exponentialRampToValueAtTime(Math.max(10, filterEndFreq), now + duration)

      const gain = ctx.createGain()
      this.applyEnvelope(gain, targetGain, endGain, duration, now)

      noise.connect(filter)
      filter.connect(gain)
      gain.connect(masterGain)

      noise.start(now)
      noise.stop(now + duration)
    }

    if (patch.oscillatorType || patch.startFreq) {
      const osc = ctx.createOscillator()
      const oscGain = ctx.createGain()
      osc.connect(oscGain)
      oscGain.connect(masterGain)

      osc.type = patch.oscillatorType ?? 'triangle'
      osc.frequency.setValueAtTime(patch.startFreq ?? 120, now)
      osc.frequency.exponentialRampToValueAtTime(Math.max(1, patch.endFreq ?? 20), now + (patch.rampDuration ?? duration))

      this.applyEnvelope(oscGain, targetGain, endGain, duration, now)

      osc.start(now)
      osc.stop(now + duration)
    }
  }

  private playFmPatch(patch: FmPatch, volumeScale = 1.0) {
    const live = this.requireContext()
    if (!live) return
    const { ctx, masterGain: outerMaster } = live

    const now = ctx.currentTime
    const carrier = ctx.createOscillator()
    const modulator = ctx.createOscillator()
    const modGain = ctx.createGain()
    const patchGain = ctx.createGain()

    carrier.type = patch.oscillatorType ?? 'sine'
    carrier.frequency.setValueAtTime(patch.frequency ?? 80, now)

    modulator.type = 'sine'
    modulator.frequency.setValueAtTime(patch.modFreq ?? 5, now)
    modGain.gain.setValueAtTime(patch.modDepth ?? 20, now)

    modulator.connect(modGain)
    modGain.connect(carrier.frequency)

    const duration = patch.duration ?? 1.0
    const targetGain = (patch.gain ?? 0.2) * volumeScale
    const endGain = patch.endGain ?? 0.01

    this.applyEnvelope(patchGain, targetGain, endGain, duration, now)

    carrier.connect(patchGain)
    patchGain.connect(outerMaster)

    modulator.start(now)
    carrier.start(now)
    modulator.stop(now + duration)
    carrier.stop(now + duration)
  }

  private playArpeggioPatch(patch: ArpeggioPatch, volumeScale = 1.0) {
    const live = this.requireContext()
    if (!live) return
    const voice = this.createOscVoice(live.masterGain, patch.oscillatorType ?? 'sine')
    if (!voice) return
    const { osc, gain, now } = voice

    const frequencies = patch.frequencies ?? [523.25, 659.25, 783.99, 1046.5]
    const noteDuration = patch.noteDuration ?? 0.08
    const totalDuration = patch.duration ?? frequencies.length * noteDuration
    const targetGain = (patch.gain ?? 0.25) * volumeScale
    const endGain = patch.endGain ?? 0.01

    frequencies.forEach((freq, index) => {
      osc.frequency.setValueAtTime(freq, now + index * noteDuration)
    })

    this.applyEnvelope(gain, targetGain, endGain, totalDuration, now)

    osc.start(now)
    osc.stop(now + totalDuration)
  }

  playScannerChirp() {
    this.playPatch('scannerChirp')
  }

  playWeapon(weaponId: WeaponId) {
    const patch = this.data.weapons[weaponId] ?? this.data.weapons.default
    this.playPatch(patch)
  }

  playExplosion(radius = 2.0) {
    const volumeScale = Math.min(1.5, Math.max(0.5, radius / 2.0))
    this.playPatch('explosion', volumeScale)
  }

  playDoorServo() {
    this.playPatch('doorSlide')
  }

  playDoorSlide() {
    this.playPatch('doorSlide')
  }

  playElevatorHum() {
    this.playPatch('elevatorHum')
  }

  playPickup() {
    this.playPatch('pickupChime')
  }

  playPickupChime() {
    this.playPatch('pickupChime')
  }

  playImpact() {
    this.playPatch('impact')
  }

  playHitBeep() {
    this.playPatch('hitBeep')
  }

  playClick() {
    this.playPatch('click')
  }

  playLaser() {
    this.playPatch('laser')
  }

  playPlayerHurt() {
    this.playPatch('playerHurt')
  }
}

export const sfx = new SoundEffects()
